import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    OutlinedInput,
    Stack,
    Typography,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import api from "../../utils/api";
import { formatMoney } from "../../utils/helpers";

function shortTeacherName(fullName, fallback = "Teacher") {
    const parts = String(fullName || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (parts.length === 0) return fallback;
    if (parts.length === 1) return parts[0];
    return `${parts[0][0]}. ${parts[parts.length - 1]}`;
}

function defaultDateRange() {
    const to = dayjs();
    const from = to.subtract(11, "month").startOf("month");
    return {
        from: from.format("YYYY-MM-DD"),
        to: to.format("YYYY-MM-DD"),
    };
}

export default function Statistics() {
    const [{ from, to }, setRange] = useState(defaultDateRange);
    const [monthly, setMonthly] = useState([]);
    const [byCourse, setByCourse] = useState([]);
    const [byTeachers, setByTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = { from, to };
            const [monthlyRes, pieRes, teachersRes] = await Promise.all([
                api.get("admin/statistics/finance/monthly", { params }),
                api.get("admin/statistics/purchases/by-course", { params }),
                api.get("admin/statistics/teachers/completed-lessons", { params }),
            ]);
            setMonthly(monthlyRes.data?.data || []);
            setByCourse(pieRes.data?.data || []);
            setByTeachers(teachersRes.data?.data || []);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    (typeof err.response?.data === "string" ? err.response.data : null) ||
                    "Failed to load statistics."
            );
            setMonthly([]);
            setByCourse([]);
            setByTeachers([]);
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    useEffect(() => {
        load();
    }, [load]);

    const lineLabels = useMemo(
        () => monthly.map((row) => dayjs(`${row.month}-01`).format("MMM YYYY")),
        [monthly]
    );

    const pieSeries = useMemo(() => {
        const palette = [
            "#1976d2",
            "#9c27b0",
            "#ed6c02",
            "#0288d1",
            "#d32f2f",
            "#2e7d32",
            "#5d4037",
            "#455a64",
        ];
        const data = byCourse.map((row, index) => ({
            id: row.course_id,
            value: row.revenue,
            label: row.course_name,
            color: palette[index % palette.length],
        }));
        return [{
            innerRadius: 45,
            outerRadius: 100,
            paddingAngle: 1,
            cornerRadius: 3,
            data,
        }];
    }, [byCourse]);

    const teachersForChart = useMemo(
        () =>
            byTeachers.map((teacher) => {
                const fullName = teacher.teacher_name || `Teacher #${teacher.teacher_id}`;
                return {
                    ...teacher,
                    full_name: fullName,
                    short_name: shortTeacherName(fullName),
                };
            }),
        [byTeachers]
    );

    const barXAxis = useMemo(() => teachersForChart.map((t) => t.short_name), [teachersForChart]);

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Statistics
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Date range
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" component="label">
                        From
                    </Typography>
                    <OutlinedInput
                        size="small"
                        type="date"
                        value={from}
                        onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                    />
                    <Typography variant="body2" component="label">
                        To
                    </Typography>
                    <OutlinedInput
                        size="small"
                        type="date"
                        value={to}
                        onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                    />
                    <Button variant="outlined" size="small" onClick={() => setRange(defaultDateRange())}>
                        Reset range
                    </Button>
                    <Button variant="contained" size="small" onClick={load} disabled={loading}>
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {error ? (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            ) : null}

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: "100%" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Income vs expenses by month
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                <CircularProgress />
                            </Box>
                        ) : monthly.length === 0 ? (
                            <Typography color="text.secondary">No ledger data in this range.</Typography>
                        ) : (
                            <Box sx={{ width: "100%", height: 400 }}>
                                <LineChart
                                    xAxis={[{ scaleType: "point", data: lineLabels, label: "" }]}
                                    series={[
                                        {
                                            data: monthly.map((r) => r.income),
                                            label: "Income",
                                            color: "#2e7d32",
                                            curve: "catmullRom",
                                            showMark: false,
                                        },
                                        {
                                            data: monthly.map((r) => r.expense),
                                            label: "Expenses",
                                            color: "#c62828",
                                            curve: "catmullRom",
                                            showMark: false,
                                        },
                                    ]}
                                    height={400}
                                    margin={{ left: 56, right: 24, top: 24, bottom: 40 }}
                                />
                            </Box>
                        )}
                    </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: "100%" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Revenue by course (purchases)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Based on course price × purchases with status processed, active, or completed.
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                <CircularProgress />
                            </Box>
                        ) : byCourse.length === 0 ? (
                            <Typography color="text.secondary">No purchases in this range.</Typography>
                        ) : (
                            <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center">
                                <Box
                                    sx={{
                                        width: "100%",
                                        maxWidth: 320,
                                        height: 320,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        overflow: "visible",
                                        flexShrink: 0,
                                    }}
                                >
                                    <PieChart
                                        series={pieSeries}
                                        width={300}
                                        height={300}
                                        margin={{ top: 20, bottom: 20, left: 20, right: 20 }}
                                        hideLegend
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200, width: "100%" }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Summary
                                    </Typography>
                                    {byCourse.map((row) => (
                                        <Typography key={row.course_id} variant="body2" sx={{ mb: 0.5 }}>
                                            {row.course_name}: {formatMoney(row.revenue)} ({row.purchase_count}{" "}
                                            {row.purchase_count === 1 ? "purchase" : "purchases"})
                                        </Typography>
                                    ))}
                                </Box>
                            </Stack>
                        )}
                    </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card sx={{ height: "100%" }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Completed lessons by teacher
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                                <CircularProgress />
                            </Box>
                        ) : byTeachers.length === 0 ? (
                            <Typography color="text.secondary">No completed lessons in this range.</Typography>
                        ) : (
                            <Box sx={{ width: "100%", height: 420 }}>
                                <BarChart
                                    xAxis={[
                                        {
                                            scaleType: "band",
                                            data: barXAxis,
                                            categoryGapRatio: 0.85,
                                            barGapRatio: 0.35,
                                            tickLabelStyle: { angle: 0, textAnchor: "middle", fontSize: 12 },
                                        },
                                    ]}
                                    yAxis={[{ valueFormatter: (value) => `${Number(value ?? 0).toLocaleString()}` }]}
                                    series={[
                                        {
                                            data: teachersForChart.map((t) => t.completed_lessons),
                                            label: "Completed lessons",
                                            color: "#1976d2",
                                        },
                                    ]}
                                    height={420}
                                    margin={{ left: 56, right: 24, top: 24, bottom: 80 }}
                                />
                            </Box>
                        )}
                    </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
